import { useQuery } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { BookOpen } from "lucide-react";
import {
  type TemplatePreviewBlocks,
  TemplateSectionPreview,
} from "@/components/template-section-preview";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/utils/trpc";
import type { AppRouter } from "../../../server/dist/routers";

type GlobalTemplateDetail =
  inferRouterOutputs<AppRouter>["forms"]["getGlobalTemplate"];

type GlobalTemplatePreviewDialogProps = {
  open: boolean;
  templateId: string | null;
  onOpenChange: (open: boolean) => void;
};

function PreviewLoadingState() {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-4 w-3/4" />
      </div>
      <Skeleton className="h-40 rounded-lg" />
      <Skeleton className="h-40 rounded-lg" />
    </div>
  );
}

function PreviewMissingState() {
  return (
    <Empty>
      <EmptyHeader>
        <EmptyTitle>Template not found</EmptyTitle>
        <EmptyDescription>
          This template is no longer available or you do not have access.
        </EmptyDescription>
      </EmptyHeader>
    </Empty>
  );
}

function GlobalTemplatePreviewDialogBody({
  templateId,
}: {
  templateId: string | null;
}) {
  const templateQuery = useQuery({
    ...trpc.forms.getGlobalTemplate.queryOptions({
      id: templateId ?? "",
    }),
    enabled: Boolean(templateId),
  });

  if (templateQuery.isLoading) {
    return <PreviewLoadingState />;
  }

  const template = templateQuery.data as GlobalTemplateDetail | undefined;

  if (!template) {
    return <PreviewMissingState />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline">Global</Badge>
        <Badge variant="outline">{template.formType}</Badge>
      </div>
      {template.description ? (
        <p className="text-sm text-muted-foreground">{template.description}</p>
      ) : null}
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <BookOpen className="h-4 w-4" />
          <span>Preview</span>
        </div>
        <TemplateSectionPreview
          blocks={template.blocks as TemplatePreviewBlocks | null | undefined}
        />
      </div>
    </div>
  );
}

export function GlobalTemplatePreviewDialog({
  open,
  templateId,
  onOpenChange,
}: GlobalTemplatePreviewDialogProps) {
  if (!open && !templateId) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          return;
        }
        onOpenChange(false);
      }}
    >
      <DialogContent className="max-h-[90vh] sm:max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Global template preview</DialogTitle>
          <DialogDescription>
            Review the template structure before cloning it into the selected
            site.
          </DialogDescription>
        </DialogHeader>
        <GlobalTemplatePreviewDialogBody templateId={templateId} />
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
